import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import gun from '../config/gun.config';
import { CapsulePin } from '../types/capsule';
import { useUser } from '../components/providers/UserProvider';

const LS_KEY_PREFIX = 'stabeon_capsule_pending_';

export function useCapsules() {
  const { user } = useUser();
  const [capsules, setCapsules] = useState<Record<string, CapsulePin>>({});
  const pendingRef = useRef<Record<string, CapsulePin>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user?.nullifierHash) return;
    const userId = user.nullifierHash;

    const mapNode = gun.get('stabeon_capsules').get(userId).map();

    mapNode.on((data: any, id: string) => {
      if (!data || !id || !data.createdAt) return;

      const capsule: CapsulePin = {
        id: data.id || id,
        creatorId: data.creatorId || userId,
        lat: Number(data.lat) || 0,
        lng: Number(data.lng) || 0,
        title: data.title || '',
        message: data.message || '',
        unlockAt: Number(data.unlockAt) || 0,
        createdAt: Number(data.createdAt) || 0,
        revealed: !!data.revealed,
        emoji: data.emoji || '🔒',
      };

      if (!capsule.lat && !capsule.lng) return;

      pendingRef.current[id] = capsule;
      if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          const batch = pendingRef.current;
          pendingRef.current = {};
          setCapsules(prev => Object.assign({}, prev, batch));
        }, 50);
      }
    });

    return () => {
      try { mapNode.off(); } catch (_) {}
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [user?.nullifierHash]);

  const sortedCapsules = useMemo(
    () => Object.values(capsules).sort((a, b) => a.unlockAt - b.unlockAt),
    [capsules]
  );

  const unrevealedUnlocked = useMemo(
    () => sortedCapsules.filter(c => c.unlockAt <= Date.now() && !c.revealed),
    [sortedCapsules]
  );

  const addCapsule = useCallback((
    lat: number,
    lng: number,
    title: string,
    message: string,
    unlockAt: number,
    emoji: string = '🔒',
  ) => {
    if (!user?.nullifierHash) return;
    const userId = user.nullifierHash;
    const now = Date.now();
    const capsuleId = `capsule_${now}_${userId.slice(-6)}`;

    const data = {
      id: capsuleId,
      creatorId: userId,
      lat,
      lng,
      title: title.trim(),
      message: message.trim(),
      unlockAt,
      createdAt: now,
      revealed: false,
      emoji,
    };

    gun.get('stabeon_capsules').get(userId).get(capsuleId).put(data as any);

    // Backup to localStorage so we can check on next open
    try {
      localStorage.setItem(`${LS_KEY_PREFIX}${capsuleId}`, JSON.stringify(data));
    } catch (_) {}
  }, [user?.nullifierHash]);

  const markRevealed = useCallback((capsuleId: string) => {
    if (!user?.nullifierHash) return;
    gun.get('stabeon_capsules').get(user.nullifierHash).get(capsuleId).get('revealed').put(true as any);
    setCapsules(prev => {
      if (!prev[capsuleId]) return prev;
      return { ...prev, [capsuleId]: { ...prev[capsuleId], revealed: true } };
    });
    // Clean up localStorage backup
    try { localStorage.removeItem(`${LS_KEY_PREFIX}${capsuleId}`); } catch (_) {}
  }, [user?.nullifierHash]);

  const deleteCapsule = useCallback((capsuleId: string) => {
    if (!user?.nullifierHash) return;
    gun.get('stabeon_capsules').get(user.nullifierHash).get(capsuleId).put(null as any);
    setCapsules(prev => {
      const next = { ...prev };
      delete next[capsuleId];
      return next;
    });
    try { localStorage.removeItem(`${LS_KEY_PREFIX}${capsuleId}`); } catch (_) {}
  }, [user?.nullifierHash]);

  return { capsules: sortedCapsules, addCapsule, markRevealed, deleteCapsule, unrevealedUnlocked };
}
